import { motion } from "framer-motion";
import {
  Anchor,
  Umbrella,
  Table,
  UtensilsCrossed,
  Zap,
  Droplets,
  TreePalm,
  Volleyball,
  Waves,
  Gamepad2,
  LucideIcon,
} from "lucide-react";
import featureDock from "@/assets/feature-dock.jpg";
import featureVolleyball from "@/assets/feature-volleyball.jpg";
import featureCabana from "@/assets/feature-cabana.jpg";
import featureBbq from "@/assets/feature-bbq.jpg";
import featureHammock from "@/assets/feature-hammock.webp";
import featureWaterslide from "@/assets/feature-waterslide.webp";
import featurePicnicTable from "@/assets/feature-picnic-table.jpg";
import featureFan from "@/assets/feature-fan.webp";
import featureWater from "@/assets/feature-water.jpg";
import featureCornhole from "@/assets/feature-cornhole.webp";

interface Feature {
  icon: LucideIcon;
  label: string;
  image?: string;
}

const features: Feature[] = [
  { icon: Anchor, label: "Private Dock with Ladder", image: featureDock },
  { icon: TreePalm, label: "Hammock", image: featureHammock },
  { icon: Table, label: "Picnic Table + Bar Top", image: featurePicnicTable },
  { icon: UtensilsCrossed, label: "Charcoal BBQ Pit", image: featureBbq },
  { icon: Zap, label: "Electricity + Fan", image: featureFan },
  { icon: Droplets, label: "Running Water", image: featureWater },
  { icon: Umbrella, label: "Shaded Cabana", image: featureCabana },
  { icon: Volleyball, label: "Beach Volleyball Access", image: featureVolleyball },
  { icon: Waves, label: "Frog Slide Access", image: featureWaterslide },
  { icon: Gamepad2, label: "Cornhole + Lawn Games", image: featureCornhole },
];

const FeaturesGrid = () => {
  return (
    <section className="py-20 lg:py-28 bg-secondary/50">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Everything You Need. Nothing You Don't.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every cabana comes fully equipped for the perfect lake day.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="bg-background rounded-2xl overflow-hidden shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300"
            >
              {feature.image ? (
                <>
                  <div className="relative">
                    <img
                      src={feature.image}
                      alt={feature.label}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute bottom-2 right-2 w-10 h-10 rounded-xl bg-background/90 backdrop-blur-sm flex items-center justify-center shadow-md">
                      <feature.icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div className="p-4 text-center">
                    <p className="text-sm font-medium text-foreground">{feature.label}</p>
                  </div>
                </>
              ) : (
                <div className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium text-foreground">{feature.label}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
